import { CommonModule, TitleCasePipe, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { NotificationFacade } from '@core/facades/notification.facade';
import { Pagination } from '@core/models/pagination.model';
import { ApprovalService } from '@core/services/approval.service';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { Store } from '@ngxs/store';
import {
  DropdownComponent,
  DropDownModel,
} from '@shared/ui/dropdown/dropdown.component';
import { FiltersComponent } from '@shared/ui/filters/filters.component';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import { TableSkeletonLoaderComponent } from '@shared/ui/table-skeleton-loader/table-skeleton-loader.component';
import { IFILTER } from 'app/app.constants';
import { TableActions } from 'app/auth/table-store/table.actions';
import { TableSelectors } from 'app/auth/table-store/table.selectors';
import { BehaviorSubject, Observable, Subscription, take } from 'rxjs';
import { PermissionDirective } from "@shared/directives/permission.directive";
import { PermissionService } from '@core/services/permission.service';
import { TableRefreshComponent } from 'app/ui/table-refresh/table-refresh.component';
@Component({
  selector: 'app-order-approval-table',
  standalone: true,
  imports: [
    FiltersComponent,
    TableNavigatorComponent,
    DropdownComponent,
    CommonModule,
    TableSkeletonLoaderComponent,
    PermissionDirective,
    TableRefreshComponent
],
  templateUrl: './order-approval-table.component.html',
  styleUrl: './order-approval-table.component.scss',
  providers: [TitleCasePipe, UpperCasePipe]
})
export class OrderApprovalTableComponent implements OnInit, OnDestroy {
  isLoading: boolean = false;
  filterMenuState: 'visible' | 'hidden' = 'hidden';
  orderListSubject = new BehaviorSubject<any>(null);
  totalCountSubject = new BehaviorSubject<number>(0);
  orderList$ = this.orderListSubject.asObservable();
  totalCount$: Observable<number> = this.totalCountSubject.asObservable();
  isFetchingOrderList$!: Observable<boolean>;

  isDropdownVisible = true;
  showDropdown = false;
  subs: Subscription[] = [];
  orderList: any[] = [];
  actionList: DropDownModel[] = [];
  totalPending = 0;
  totalApproved = 0;
  totalReq = 0;
  totalCount!: number;
  hadFilterBefore: boolean = false;
  isFilterActive: boolean = false;

  currentPage: number = 1;

  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  notify = inject(NotificationFacade);
  approval = inject(ApprovalService);
  change = inject(ChangeDetectorRef);
  breadCrumbService = inject(BreadCrumbService);
  store = inject(Store);
  confirmDialogService = inject(ConfirmDialogService);
  permissionService = inject(PermissionService);
  $index: any;

  constructor(private titleCasePipe: TitleCasePipe, private upperCasePipe: UpperCasePipe) {
    this.orderList$ = this.store.select(
      TableSelectors.getTableData('orderApprovals')
    );
    this.totalCount$ = this.store.select(
      TableSelectors.getTableTotalCount('orderApprovals')
    );
    this.isFetchingOrderList$ = this.store.select(
      TableSelectors.getTableLoading('orderApprovals')
    );
  }

  filterStructure: IFILTER[] = [
    {
      filterKey: 'start_date',
      label: 'Start Date',
      type: 'date',
    },
    {
      filterKey: 'end_date',
      label: 'End Date',
      type: 'date',
    },
    {
      filterKey: 'status',
      label: 'Status',
      type: 'dropdown',
      dropdown: [
        { key: 'pending', value: 'Pending' },
        { key: 'approved', value: 'Approved' },
        { key: 'rejected', value: 'Rejected' },
      ],
    },
  ];

  ngOnInit(): void {
    this.breadCrumbService.setPageBreadCrumb(this.activatedRoute);
    this.filterEventListener();
    this.loadOrdersList();
  }
  initDropdownList(param: any) {
    return (this.actionList = [
      {
        action: () => {
          this.confirmDialogService.openDialog({
            isOpen: true,
            isDialogLoading: false,
            title: 'Yes, Approve Order',
            description: 'Are you sure you want to approve this order?',
            loadingText: 'Approving Order, please wait...',
            action: () => {
              this.approveOrRejectOrder('approved', param.id);
            },
          });
        },
    
        title: 'Approve',
        show: param.status !== 'approved' && this.permissionService.hasPermission(['approve_order']),
      },
      {
        action: () => {
           this.confirmDialogService.openDialog({
            isOpen: true,
            isDialogLoading: false,
            title: 'Yes, Reject Order',
            description: 'Are you sure you want to reject this order?',
            loadingText: 'Rejecting Order, please wait...',
            action: () => {
              this.approveOrRejectOrder('rejected', param.id);
            },
          });
        },
        title: 'Reject',
        show: param.status !== 'rejected' && this.permissionService.hasPermission(['reject_order']),
      },
    ]);
  }

  loadOrdersList(page = 1) {
    const sub = this.orderList$.pipe(take(1)).subscribe((order) => {
      const hasData = Array.isArray(order) && order.length > 0;
      // only dispatch if store is empty
      if (!hasData) {
        this.store.dispatch(
          new TableActions.LoadTableData({
            tableKey: 'orderApprovals',
            params: new Pagination({
              skip: (page - 1) * 10,
              size: 10,
            }),
          })
        );
      }
    });
    this.subs.push(sub);
  }

  filterEventListener() {
    this.activatedRoute.queryParams.subscribe((params) => {
      let query = '';
      if (params[this.filterStructure[0].filterKey]) {
        query = `start_date=${params['start_date']}&`;
      }
      if (params[this.filterStructure[1].filterKey]) {
        query += `end_date=${params['end_date']}&`;
      }
      if (params[this.filterStructure[2].filterKey]) {
        query += `status=${params['status']}&`;
      }
      if (params['search']) {
        query += `search_text=${params['search']}&`;
      }

      // Handle pagination
      const rawPage = params['page'] ? parseInt(params['page']) : 1;
      const page = rawPage > 1 ? rawPage - 1 : 0;
      const take = 10;
      const skip = page * take;

      if (query) {
        query += `skip=${skip}&take=${take}`;
        this.isFilterActive = true;
        this.hadFilterBefore = true;
        this.store.dispatch(
          new TableActions.FilterOrderApprovalList({ filter: query })
        );
      } else {
        // only re-fetch if there was a previous filter
        if (this.hadFilterBefore || params['page']) {
          this.hadFilterBefore = false;
          this.store.dispatch(
            new TableActions.LoadTableData({
              tableKey: 'orderApprovals',
              params: new Pagination({ skip: skip, size: take }),
            })
          );
        }
      }
    });
  }

  getJointAccountName(account: any[]) {
    return account.reduce((p, c, i) => {
      if (i > 0) {
        return (
          p +
          ` & ${c.personal_details.first_name} ${c.personal_details.last_name.toLocaleUpperCase()}`
        );
      }
      return `${c.personal_details.first_name} ${c.personal_details.last_name.toLocaleUpperCase()}`;
    }, '');
  }

  navigate(currentPage: number = 1) {
    this.isLoading = true;
    this.currentPage = currentPage;
    this.router.navigate([], {
      queryParams: { page: currentPage },
      queryParamsHandling: 'merge',
    });
  }

  approveOrRejectOrder(action: 'approved' | 'rejected', orderId: string) {
    this.confirmDialogService.updateDialog({ isDialogLoading: true });
      const sub = this.store
        .dispatch(new TableActions.ApproveOrRejectOrder({ id: orderId, action }))
        .subscribe({
          complete: () => {
            // This runs when the action completes (success or error)
            this.confirmDialogService.updateDialog({
              isDialogLoading: false,
              isOpen: false,
            });
            this.notify.success(`Transaction ${action} successfully`);
            this.change.detectChanges();
          },
          error: (err) => {
            this.confirmDialogService.updateDialog({ isDialogLoading: false, isOpen: false });
            this.change.detectChanges();
          },
        });
      this.subs.push(sub);
    
  }

  formatOrderName(name: string): string {
    if (!name) return '-';
    
    const parts = name.trim().split(' ');
    
    if (parts.length === 0) return '-';
    if (parts.length === 1) return this.titleCasePipe.transform(parts[0]) || '';
    if (parts.length === 2) {
      // Only first and last name
      const firstName = this.titleCasePipe.transform(parts[0]) || '';
      const lastName = this.upperCasePipe.transform(parts[1]) || '';
      return `${firstName} ${lastName}`.trim();
    }
    
    const firstName = this.upperCasePipe.transform(parts[0]) || '';
    const middleAndLastNames = parts.slice(1)
      .map(name => this.titleCasePipe.transform(name))
      .join(' ');
    
    return `${middleAndLastNames} ${firstName}`.trim();
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
